import { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Image as ImageIcon, AlertTriangle, Upload, PlusCircle, Copy } from 'lucide-react';
import { SelecaoUsuario } from './selecao-usuario';
import { ModalCopiarDadosMaquina } from './modal-copiar-dados-maquina';
import { AssinaturaManager } from './assinatura-manager';
import { Etapa1DadosMaquinaPraxis } from './etapa1-dados-maquina-praxis';
import { ListaVerificacaoAdministrativa as ListaVerificacaoComponent } from './lista-verificacao-administrativa';
import { useProjeto } from '@/contexts/projeto-hook';
import { ListaVerificacaoAdministrativa as ListaVerificacaoType } from '@/types/lista-verificacao';
import { apiService } from '@/services/api';
import { converterHeicParaJpeg } from '@/utils/heic-converter';
import { gerarPlacaIdentificacaoFile } from '@/utils/gerar-placa-identificacao';
import { useI18n } from '@/contexts/i18n-hook';
import { USUARIO_PADRAO_LAUAN, USUARIO_PADRAO_LUCIANO, USUARIO_PADRAO_JOAO, USUARIO_PADRAO_DAVI } from '@/constants/usuarios-padrao';

// Componente para exibir imagem com tratamento de erro
function ImagemComFallback({
  src,
  alt,
  className
}: {
  src: string | undefined;
  alt: string;
  className?: string;
}) {
  const { t } = useI18n();
  const [erroCarregamento, setErroCarregamento] = useState(false);
  const [carregando, setCarregando] = useState(true);

  if (!src) {
    return (
      <div className={`flex items-center justify-center ${className || ''}`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.arquivo.nenhumEscolhido')}</p>
      </div>
    );
  }

  if (erroCarregamento) {
    return (
      <div className={`flex flex-col items-center justify-center ${className || ''}`}>
        <AlertTriangle className="h-8 w-8 text-yellow-500 mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('apreciacaoEdicao.dadosMaquina.imagem.erroCarregar')}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {carregando && (
        <div className={`absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-schmersal-gray-dark ${className || ''}`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.estado.carregando')}</p>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className || ''} ${carregando ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onLoad={() => setCarregando(false)}
        onError={() => {
          setErroCarregamento(true);
          setCarregando(false);
        }}
      />
    </div>
  );
}

interface Etapa1DadosMaquinaProps {
  dados: any;
  onChange?: (campo: string, valor: any) => void;
  modoEdicao?: boolean;
  apreciacaoId?: string;
}

export function Etapa1DadosMaquina(props: Etapa1DadosMaquinaProps) {
  const { projeto } = useProjeto();
  const clienteTipo = props.dados?.projeto?.clienteTipo || props.dados?.clienteTipo || projeto?.clienteTipo || 'arcelor';

  if (clienteTipo === 'praxis') {
    return <Etapa1DadosMaquinaPraxis {...props} />;
  }

  return <Etapa1DadosMaquinaArcelor {...props} />;
}

function Etapa1DadosMaquinaArcelor({
  dados,
  onChange,
  modoEdicao = false,
  apreciacaoId,
}: Etapa1DadosMaquinaProps) {
  const { t, formatarData } = useI18n();
  const { projeto } = useProjeto();
  const empresaId = dados?.projeto?.empresaId || projeto?.empresaId;
  const [uploadingPanoramica, setUploadingPanoramica] = useState(false);
  const [uploadingIdentificacao, setUploadingIdentificacao] = useState(false);
  const [draggingPanoramica, setDraggingPanoramica] = useState(false);
  const [draggingIdentificacao, setDraggingIdentificacao] = useState(false);
  const [gerandoIdentificacao, setGerandoIdentificacao] = useState(false);
  const [mostrarFormIdentificacao, setMostrarFormIdentificacao] = useState(false);
  const [formIdentificacao, setFormIdentificacao] = useState({ descricao: '', sismana: '' });
  const fileInputPanoramicaRef = useRef<HTMLInputElement>(null);
  const fileInputIdentificacaoRef = useRef<HTMLInputElement>(null);

  const [executanteUsuario, setExecutanteUsuario] = useState<any>(null);
  const [responsavelTecnicoUsuario, setResponsavelTecnicoUsuario] = useState<any>(null);
  const [verificadorUsuario, setVerificadorUsuario] = useState<any>(null);
  const [modalCopiarAberto, setModalCopiarAberto] = useState(false);

  // Carregar dados do usuário quando executante/elaborador é selecionado
  useEffect(() => {
    const userId = dados.executanteUserId || dados.elaboradorUserId;
    if (userId && !dados.executanteExterno && !dados.elaboradorExterno) {
      apiService.buscarUsuario(userId).then(response => {
        if (response.success && response.data) {
          setExecutanteUsuario(response.data);
        }
      });
    } else {
      setExecutanteUsuario(null);
    }
  }, [dados.executanteUserId, dados.elaboradorUserId, dados.executanteExterno, dados.elaboradorExterno]);

  // Carregar dados do usuário quando responsável técnico é selecionado (independente de verificador/aprovador)
  useEffect(() => {
    const userId = dados.responsavelTecnicoUserId;
    if (userId && !dados.responsavelTecnicoExterno) {
      apiService.buscarUsuario(userId).then(response => {
        if (response.success && response.data) {
          setResponsavelTecnicoUsuario(response.data);
        }
      });
    } else {
      setResponsavelTecnicoUsuario(null);
    }
  }, [dados.responsavelTecnicoUserId, dados.responsavelTecnicoExterno]);

  // Carregar dados do usuário quando verificador é selecionado
  useEffect(() => {
    const userId = dados.verificadorUserId;
    if (userId && !dados.verificadorExterno) {
      apiService.buscarUsuario(userId).then(response => {
        if (response.success && response.data) {
          setVerificadorUsuario(response.data);
        }
      });
    } else {
      setVerificadorUsuario(null);
    }
  }, [dados.verificadorUserId, dados.verificadorExterno]);

  const handleUsuarioChange = (tipo: 'elaborador' | 'verificador' | 'aprovador' | 'executante' | 'responsavelTecnico') => {
    return async (usuarioId: string | null, usuarioExterno: any) => {
      if (usuarioId) {
        if (tipo === 'verificador') setVerificadorUsuario(null);
        else if (tipo === 'responsavelTecnico') setResponsavelTecnicoUsuario(null);
        onChange?.(`${tipo}UserId`, usuarioId);
        onChange?.(`${tipo}Externo`, null);

        if (tipo === 'executante' || tipo === 'responsavelTecnico' || tipo === 'verificador') {
          try {
            const response = await apiService.buscarUsuario(usuarioId);
            if (response.success && response.data) {
              if (tipo === 'executante') {
                setExecutanteUsuario(response.data);
              } else if (tipo === 'responsavelTecnico') {
                setResponsavelTecnicoUsuario(response.data);
              } else if (tipo === 'verificador') {
                setVerificadorUsuario(response.data);
              }
            }
          } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
          }
        }
      } else if (usuarioExterno) {
        onChange?.(`${tipo}Externo`, usuarioExterno);
        onChange?.(`${tipo}UserId`, null);
        if (tipo === 'executante') {
          setExecutanteUsuario(null);
        } else if (tipo === 'responsavelTecnico') {
          setResponsavelTecnicoUsuario(null);
        } else if (tipo === 'verificador') {
          setVerificadorUsuario(null);
        }
      } else {
        onChange?.(`${tipo}UserId`, null);
        onChange?.(`${tipo}Externo`, null);
        if (tipo === 'executante') {
          setExecutanteUsuario(null);
        } else if (tipo === 'responsavelTecnico') {
          setResponsavelTecnicoUsuario(null);
        } else if (tipo === 'verificador') {
          setVerificadorUsuario(null);
        }
      }
    };
  };

  const handleListaVerificacaoChange = (lista: ListaVerificacaoType) => {
    onChange?.('listaVerificacaoAdministrativa', lista);
  };

  const handleUploadFotoInformacaoMaquina = useCallback(async (
    file: File,
    tipo: 'panoramica' | 'identificacao'
  ) => {
    if (!apreciacaoId) {
      alert(t('apreciacaoEdicao.dadosMaquina.upload.salvePrimeiro'));
      return;
    }

    const isPanoramica = tipo === 'panoramica';
    const setUploading = isPanoramica ? setUploadingPanoramica : setUploadingIdentificacao;
    const campoDestino = isPanoramica ? 'fotoPanoramicaUrl' : 'fotoIdentificacaoUrl';
    const mensagemErro = isPanoramica
      ? t('apreciacaoEdicao.dadosMaquina.upload.erroPanoramica')
      : t('apreciacaoEdicao.dadosMaquina.upload.erroIdentificacao');

    setUploading(true);
    try {
      const fileParaUpload = await converterHeicParaJpeg(file);
      const response = isPanoramica
        ? await apiService.uploadFotoPanoramica(apreciacaoId, fileParaUpload)
        : await apiService.uploadFotoIdentificacao(apreciacaoId, fileParaUpload);

      if (response.success && response.data) {
        const envUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const baseUrl = envUrl.replace(/\/api$/, '');
        const imageUrl = response.data.url.startsWith('http')
          ? response.data.url
          : `${baseUrl}${response.data.url}`;
        onChange?.(campoDestino, imageUrl);
      } else {
        alert(response.message || mensagemErro);
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert(error instanceof Error ? error.message : mensagemErro);
    } finally {
      setUploading(false);
    }
  }, [apreciacaoId, onChange, t]);

  const handleDropFotoInformacaoMaquina = async (
    e: React.DragEvent<HTMLDivElement>,
    tipo: 'panoramica' | 'identificacao'
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (tipo === 'panoramica') {
      setDraggingPanoramica(false);
    } else {
      setDraggingIdentificacao(false);
    }

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) {
      alert(t('apreciacaoEdicao.dadosMaquina.upload.arrasteVazio'));
      return;
    }

    const imageFile = files.find((f) => f.type?.startsWith('image/'));
    if (!imageFile) {
      alert(t('apreciacaoEdicao.dadosMaquina.upload.arquivoInvalido'));
      return;
    }

    await handleUploadFotoInformacaoMaquina(imageFile, tipo);
  };

  // Abre o form inline para preencher dados antes de gerar
  const handleCriarIdentificacao = useCallback(() => {
    if (!apreciacaoId) {
      alert(t('apreciacaoEdicao.dadosMaquina.identificacao.salvePrimeiro'));
      return;
    }
    // Pre-preencher com dados já existentes
    setFormIdentificacao({
      descricao: dados.maquina?.descricao || dados.identificacaoMaquina || '',
      sismana: dados.ativo?.refSismanaNovo || dados.ativo?.refSismanaAntigo || dados.identificacaoMaquina || '',
    });
    setMostrarFormIdentificacao(true);
  }, [apreciacaoId, dados, t]);

  // Gerar foto de identificação (placa) e enviar.
  // O desenho vive em utils/gerar-placa-identificacao, compartilhado com o
  // job de criação assistida por IA — que gera a mesma placa no backend.
  const gerarFotoIdentificacao = useCallback(async (descricao: string, sismana: string) => {
    if (!apreciacaoId) return;

    setGerandoIdentificacao(true);
    setMostrarFormIdentificacao(false);
    try {
      const file = await gerarPlacaIdentificacaoFile({ descricao, sismana });
      await handleUploadFotoInformacaoMaquina(file, 'identificacao');
    } catch (error) {
      console.error('Erro ao gerar foto de identificação:', error);
      alert(t('apreciacaoEdicao.dadosMaquina.identificacao.erroGerar'));
    } finally {
      setGerandoIdentificacao(false);
    }
  }, [apreciacaoId, handleUploadFotoInformacaoMaquina, t]);

  // Copiar todos os dados da etapa (Etapa 1) a partir de outra apreciação
  const handleCopiarDeApreciacao = useCallback((origem: any) => {
    if (!origem) return;

    // Converte URL relativa de imagem em URL completa (mesmo critério do carregamento)
    const construirUrlCompleta = (url: string | null | undefined): string => {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:image')) {
        return url;
      }
      const envUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const baseUrl = envUrl.replace(/\/api$/, '');
      return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
    };
    const soData = (val: any): string => (val ? String(val).substring(0, 10) : '');

    // REVISÃO
    onChange?.('numeroArcelorMittal', origem.numeroArcelorMittal || '');
    onChange?.('demanda', origem.demanda || '');
    onChange?.('dataEmissao', soData(origem.dataEmissao));

    // Executante (Elaborador)
    onChange?.('executanteUserId', origem.executanteUserId || null);
    onChange?.('executanteExterno', origem.executanteExterno || null);
    onChange?.('elaboradorUserId', origem.elaboradorUserId || null);
    onChange?.('elaboradorExterno', origem.elaboradorExterno || null);
    onChange?.('dataElaboracao', soData(origem.dataElaboracao));

    // Verificador
    onChange?.('verificadorUserId', origem.verificadorUserId || null);
    onChange?.('verificadorExterno', origem.verificadorExterno || null);
    onChange?.('dataVerificacao', soData(origem.dataVerificacao));

    // Responsável Técnico
    onChange?.('responsavelTecnicoUserId', origem.responsavelTecnicoUserId || null);
    onChange?.('responsavelTecnicoExterno', origem.responsavelTecnicoExterno || null);
    onChange?.('dataAprovacao', soData(origem.dataAprovacao));

    // Fotos da máquina
    onChange?.('fotoPanoramicaUrl', construirUrlCompleta(origem.fotoPanoramicaUrl));
    onChange?.('fotoIdentificacaoUrl', construirUrlCompleta(origem.fotoIdentificacaoUrl));

    // Identificação da máquina (campos exibidos no Cabeçalho / Identificação Documental)
    onChange?.('nomeMaquina', origem.nomeMaquina || '');
    onChange?.('tipoMaquina', origem.tipoMaquina || '');
    onChange?.('setorMaquina', origem.setorMaquina || '');

    // C.1 Informações da máquina
    onChange?.('identificacaoMaquina', origem.identificacaoMaquina || '');
    onChange?.('maquina', origem.maquina ? { ...origem.maquina } : {});

    // C.2 Responsável pela máquina
    onChange?.('operadorEntrevistado', origem.operadorEntrevistado || '');
    onChange?.('cargoOperador', origem.cargoOperador || '');
    onChange?.('dataEntrevista', soData(origem.dataEntrevista));

    // C.3 Limites da máquina
    onChange?.('limitesUso', origem.limitesUso || '');
    onChange?.('limitesEspaco', origem.limitesEspaco || '');
    onChange?.('limitesAdicionais', origem.limitesAdicionais || '');
    onChange?.('historicoAcidentes', origem.historicoAcidentes || '');

    // Lista de verificação administrativa
    if (origem.listaVerificacaoAdministrativa) {
      onChange?.('listaVerificacaoAdministrativa', origem.listaVerificacaoAdministrativa);
    }
  }, [onChange]);

  return (
    <div className="space-y-6">
      {/* Copiar dados de outra máquina */}
      {modoEdicao && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setModalCopiarAberto(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md
                     border border-schmersal-blue text-schmersal-blue dark:text-white
                     bg-white dark:bg-schmersal-gray-darkest
                     hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-blue transition-all duration-200"
            title={t('apreciacaoEdicao.dadosMaquina.copiar.titulo')}
          >
            <Copy className="h-4 w-4" />
            {t('apreciacaoEdicao.dadosMaquina.copiar.botao')}
          </button>
          <ModalCopiarDadosMaquina
            aberto={modalCopiarAberto}
            apreciacaoIdAtual={apreciacaoId}
            onFechar={() => setModalCopiarAberto(false)}
            onSelecionar={(origem) => {
              if (window.confirm(t('apreciacaoEdicao.dadosMaquina.copiar.confirmacao'))) {
                handleCopiarDeApreciacao(origem);
              }
            }}
          />
        </div>
      )}

      {/* REVISÃO */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <FileText className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
          {t('apreciacaoEdicao.dadosMaquina.revisao.titulo')}
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.revisao.numeroArcelorMittal')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.numeroArcelorMittal || ''}
                onChange={(e) => onChange?.('numeroArcelorMittal', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.numeroArcelorMittal || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.revisao.demanda')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.demanda || ''}
                onChange={(e) => onChange?.('demanda', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.demanda || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.revisao.data')}
            </label>
            {modoEdicao ? (
              <input
                type="date"
                value={dados.dataEmissao ? String(dados.dataEmissao).substring(0, 10) : ''}
                onChange={(e) => onChange?.('dataEmissao', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.dataEmissao ? formatarData(dados.dataEmissao, { timeZone: 'UTC' }) : '-'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Info Executante (Elaborador) */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.dadosMaquina.executante.titulo')}</h2>
        {modoEdicao ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 self-center mr-1">{t('apreciacaoEdicao.dadosMaquina.usuarioPadrao')}</span>
              <button
                type="button"
                onClick={() => {
                  onChange?.('executanteExterno', { ...USUARIO_PADRAO_LAUAN });
                  onChange?.('executanteUserId', null);
                  onChange?.('elaboradorExterno', { ...USUARIO_PADRAO_LAUAN });
                  onChange?.('elaboradorUserId', null);
                  setExecutanteUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Lauan' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('executanteExterno', { ...USUARIO_PADRAO_LUCIANO });
                  onChange?.('executanteUserId', null);
                  onChange?.('elaboradorExterno', { ...USUARIO_PADRAO_LUCIANO });
                  onChange?.('elaboradorUserId', null);
                  setExecutanteUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Luciano' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('executanteExterno', { ...USUARIO_PADRAO_JOAO });
                  onChange?.('executanteUserId', null);
                  onChange?.('elaboradorExterno', { ...USUARIO_PADRAO_JOAO });
                  onChange?.('elaboradorUserId', null);
                  setExecutanteUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'João' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('executanteExterno', { ...USUARIO_PADRAO_DAVI });
                  onChange?.('executanteUserId', null);
                  onChange?.('elaboradorExterno', { ...USUARIO_PADRAO_DAVI });
                  onChange?.('elaboradorUserId', null);
                  setExecutanteUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Davi' })}
              </button>
            </div>
            <SelecaoUsuario
              label={t('apreciacaoEdicao.dadosMaquina.executante.selecaoLabel')}
              rotuloNome={t('apreciacaoEdicao.dadosMaquina.executante.rotuloNome')}
              empresaId={empresaId || undefined}
              usuarioId={dados.executanteUserId || dados.elaboradorUserId}
              usuarioExterno={dados.executanteExterno || dados.elaboradorExterno}
              onChange={(usuarioId, usuarioExterno) => {
                // Quando selecionar executante, atualizar elaborador também
                if (usuarioId) {
                  setExecutanteUsuario(null);
                  onChange?.('executanteUserId', usuarioId);
                  onChange?.('executanteExterno', null);
                  onChange?.('elaboradorUserId', usuarioId);
                  onChange?.('elaboradorExterno', null);
                  apiService.buscarUsuario(usuarioId).then(response => {
                    if (response.success && response.data) {
                      setExecutanteUsuario(response.data);
                    }
                  });
                } else if (usuarioExterno) {
                  onChange?.('executanteExterno', usuarioExterno);
                  onChange?.('executanteUserId', null);
                  onChange?.('elaboradorExterno', usuarioExterno);
                  onChange?.('elaboradorUserId', null);
                  setExecutanteUsuario(null);
                } else {
                  onChange?.('executanteUserId', null);
                  onChange?.('executanteExterno', null);
                  onChange?.('elaboradorUserId', null);
                  onChange?.('elaboradorExterno', null);
                  setExecutanteUsuario(null);
                }
              }}
              modoEdicao={modoEdicao}
            />
            <AssinaturaManager
              assinaturaUrl={dados.executanteExterno?.assinaturaUrl || dados.elaboradorExterno?.assinaturaUrl || executanteUsuario?.assinaturaUrl || dados.executante?.assinaturaUrl || dados.elaborador?.assinaturaUrl}
              assinaturaDigital={dados.executanteExterno?.assinaturaDigital || dados.elaboradorExterno?.assinaturaDigital || executanteUsuario?.assinaturaDigital || dados.executante?.assinaturaDigital || dados.elaborador?.assinaturaDigital}
              onChange={async (url, digital) => {
                const userId = dados.executanteUserId || dados.elaboradorUserId;
                if (dados.executanteExterno || dados.elaboradorExterno) {
                  // Se for externo, salvar apenas na apreciação
                  const externo = dados.executanteExterno || dados.elaboradorExterno;
                  onChange?.('executanteExterno', {
                    ...externo,
                    assinaturaUrl: url,
                    assinaturaDigital: digital,
                  });
                  onChange?.('elaboradorExterno', {
                    ...externo,
                    assinaturaUrl: url,
                    assinaturaDigital: digital,
                  });
                } else if (userId) {
                  // Se for usuário do sistema, salvar no perfil do usuário também
                  try {
                    await apiService.atualizarAssinaturaUsuario(userId, {
                      assinaturaUrl: url,
                      assinaturaDigital: digital,
                    });
                    // Atualizar estado local
                    setExecutanteUsuario((prev: any) => ({
                      ...prev,
                      assinaturaUrl: url,
                      assinaturaDigital: digital,
                    }));
                  } catch (error) {
                    console.error('Erro ao salvar assinatura do usuário:', error);
                    alert(t('apreciacaoEdicao.dadosMaquina.assinatura.erroSalvar'));
                  }
                }
              }}
              modoEdicao={modoEdicao}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.executante.dataElaboracao')}
              </label>
              {modoEdicao ? (
                <input
                  type="date"
                  value={dados.dataElaboracao ? String(dados.dataElaboracao).substring(0, 10) : ''}
                  onChange={(e) => onChange?.('dataElaboracao', e.target.value)}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">
                  {dados.dataElaboracao ? formatarData(dados.dataElaboracao, { timeZone: 'UTC' }) : '-'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-900 dark:text-white">
              <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.nome')}</strong> {dados.executanteExterno?.nome || dados.elaboradorExterno?.nome || dados.executante?.nome || dados.elaborador?.nome || '-'}
            </p>
            {(dados.executanteExterno?.crea || dados.elaboradorExterno?.crea || dados.executante?.crea || dados.elaborador?.crea) && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.crea')}</strong> {dados.executanteExterno?.crea || dados.elaboradorExterno?.crea || dados.executante?.crea || dados.elaborador?.crea}
              </p>
            )}
            {(dados.executanteExterno?.cargo || dados.elaboradorExterno?.cargo || dados.executante?.cargo || dados.elaborador?.cargo) && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.cargo')}</strong> {dados.executanteExterno?.cargo || dados.elaboradorExterno?.cargo || dados.executante?.cargo || dados.elaborador?.cargo}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Info Verificador */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.dadosMaquina.verificador.titulo')}</h2>
        {modoEdicao ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 self-center mr-1">{t('apreciacaoEdicao.dadosMaquina.usuarioPadrao')}</span>
              <button
                type="button"
                onClick={() => {
                  onChange?.('verificadorExterno', { ...USUARIO_PADRAO_LAUAN });
                  onChange?.('verificadorUserId', null);
                  setVerificadorUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Lauan' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('verificadorExterno', { ...USUARIO_PADRAO_LUCIANO });
                  onChange?.('verificadorUserId', null);
                  setVerificadorUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Luciano' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('verificadorExterno', { ...USUARIO_PADRAO_JOAO });
                  onChange?.('verificadorUserId', null);
                  setVerificadorUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'João' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('verificadorExterno', { ...USUARIO_PADRAO_DAVI });
                  onChange?.('verificadorUserId', null);
                  setVerificadorUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Davi' })}
              </button>
            </div>
            <SelecaoUsuario
              label={t('apreciacaoEdicao.dadosMaquina.verificador.rotulo')}
              rotuloNome={t('apreciacaoEdicao.dadosMaquina.verificador.rotulo')}
              empresaId={empresaId || undefined}
              usuarioId={dados.verificadorUserId}
              usuarioExterno={dados.verificadorExterno}
              onChange={handleUsuarioChange('verificador')}
              modoEdicao={modoEdicao}
            />
            <AssinaturaManager
              assinaturaUrl={dados.verificadorExterno?.assinaturaUrl || verificadorUsuario?.assinaturaUrl || dados.verificador?.assinaturaUrl}
              assinaturaDigital={dados.verificadorExterno?.assinaturaDigital || verificadorUsuario?.assinaturaDigital || dados.verificador?.assinaturaDigital}
              onChange={async (url, digital) => {
                const userId = dados.verificadorUserId;
                if (dados.verificadorExterno) {
                  // Se for externo, salvar apenas na apreciação
                  onChange?.('verificadorExterno', {
                    ...dados.verificadorExterno,
                    assinaturaUrl: url,
                    assinaturaDigital: digital,
                  });
                } else if (userId) {
                  // Se for usuário do sistema, salvar no perfil do usuário também
                  try {
                    await apiService.atualizarAssinaturaUsuario(userId, {
                      assinaturaUrl: url,
                      assinaturaDigital: digital,
                    });
                    // Atualizar estado local
                    setVerificadorUsuario((prev: any) => ({
                      ...prev,
                      assinaturaUrl: url,
                      assinaturaDigital: digital,
                    }));
                  } catch (error) {
                    console.error('Erro ao salvar assinatura do usuário:', error);
                    alert(t('apreciacaoEdicao.dadosMaquina.assinatura.erroSalvar'));
                  }
                }
              }}
              modoEdicao={modoEdicao}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.verificador.dataVerificacao')}
              </label>
              {modoEdicao ? (
                <input
                  type="date"
                  value={dados.dataVerificacao ? String(dados.dataVerificacao).substring(0, 10) : ''}
                  onChange={(e) => onChange?.('dataVerificacao', e.target.value)}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">
                  {dados.dataVerificacao ? formatarData(dados.dataVerificacao, { timeZone: 'UTC' }) : '-'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-900 dark:text-white">
              <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.nome')}</strong> {dados.verificadorExterno?.nome || dados.verificador?.nome || '-'}
            </p>
            {dados.verificadorExterno?.crea && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.crea')}</strong> {dados.verificadorExterno.crea}
              </p>
            )}
            {dados.verificadorExterno?.cargo && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.cargo')}</strong> {dados.verificadorExterno.cargo}
              </p>
            )}
            {dados.dataVerificacao && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.dataVerificacao')}</strong> {formatarData(dados.dataVerificacao, { timeZone: 'UTC' })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Info Responsável Técnico */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.dadosMaquina.responsavelTecnico.titulo')}</h2>
        {modoEdicao ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 self-center mr-1">{t('apreciacaoEdicao.dadosMaquina.usuarioPadrao')}</span>
              <button
                type="button"
                onClick={() => {
                  onChange?.('responsavelTecnicoExterno', { ...USUARIO_PADRAO_LAUAN });
                  onChange?.('responsavelTecnicoUserId', null);
                  setResponsavelTecnicoUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Lauan' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('responsavelTecnicoExterno', { ...USUARIO_PADRAO_LUCIANO });
                  onChange?.('responsavelTecnicoUserId', null);
                  setResponsavelTecnicoUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Luciano' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('responsavelTecnicoExterno', { ...USUARIO_PADRAO_JOAO });
                  onChange?.('responsavelTecnicoUserId', null);
                  setResponsavelTecnicoUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'João' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.('responsavelTecnicoExterno', { ...USUARIO_PADRAO_DAVI });
                  onChange?.('responsavelTecnicoUserId', null);
                  setResponsavelTecnicoUsuario(null);
                }}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark"
              >
                {t('apreciacaoEdicao.dadosMaquina.usarUsuario', { nome: 'Davi' })}
              </button>
            </div>
            <SelecaoUsuario
              label={t('apreciacaoEdicao.dadosMaquina.responsavelTecnico.rotulo')}
              rotuloNome={t('apreciacaoEdicao.dadosMaquina.responsavelTecnico.rotulo')}
              empresaId={empresaId || undefined}
              usuarioId={dados.responsavelTecnicoUserId}
              usuarioExterno={dados.responsavelTecnicoExterno}
              onChange={handleUsuarioChange('responsavelTecnico')}
              modoEdicao={modoEdicao}
            />
            <AssinaturaManager
              assinaturaUrl={dados.responsavelTecnicoExterno?.assinaturaUrl || responsavelTecnicoUsuario?.assinaturaUrl || dados.responsavelTecnico?.assinaturaUrl}
              assinaturaDigital={dados.responsavelTecnicoExterno?.assinaturaDigital || responsavelTecnicoUsuario?.assinaturaDigital || dados.responsavelTecnico?.assinaturaDigital}
              onChange={async (url, digital) => {
                const userId = dados.responsavelTecnicoUserId;
                const externo = dados.responsavelTecnicoExterno;
                if (externo) {
                  onChange?.('responsavelTecnicoExterno', {
                    ...externo,
                    assinaturaUrl: url,
                    assinaturaDigital: digital,
                  });
                } else if (userId) {
                  try {
                    await apiService.atualizarAssinaturaUsuario(userId, {
                      assinaturaUrl: url,
                      assinaturaDigital: digital,
                    });
                    setResponsavelTecnicoUsuario((prev: any) => ({
                      ...prev,
                      assinaturaUrl: url,
                      assinaturaDigital: digital,
                    }));
                  } catch (error) {
                    console.error('Erro ao salvar assinatura do usuário:', error);
                    alert(t('apreciacaoEdicao.dadosMaquina.assinatura.erroSalvar'));
                  }
                }
              }}
              modoEdicao={modoEdicao}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.responsavelTecnico.dataAprovacao')}
              </label>
              {modoEdicao ? (
                <input
                  type="date"
                  value={dados.dataAprovacao ? String(dados.dataAprovacao).substring(0, 10) : ''}
                  onChange={(e) => onChange?.('dataAprovacao', e.target.value)}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">
                  {dados.dataAprovacao ? formatarData(dados.dataAprovacao, { timeZone: 'UTC' }) : '-'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-900 dark:text-white">
              <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.nome')}</strong> {dados.responsavelTecnicoExterno?.nome || dados.responsavelTecnico?.nome || '-'}
            </p>
            {dados.responsavelTecnicoExterno?.crea && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.crea')}</strong> {dados.responsavelTecnicoExterno.crea}
              </p>
            )}
            {dados.responsavelTecnicoExterno?.cargo && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.cargo')}</strong> {dados.responsavelTecnicoExterno.cargo}
              </p>
            )}
            {dados.dataAprovacao && (
              <p className="text-sm text-gray-900 dark:text-white">
                <strong>{t('apreciacaoEdicao.dadosMaquina.pessoa.dataAprovacao')}</strong> {formatarData(dados.dataAprovacao, { timeZone: 'UTC' })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Informações da máquina - Fotos */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <ImageIcon className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
          {t('apreciacaoEdicao.dadosMaquina.fotos.titulo')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Foto panorâmica */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.fotos.panoramica')}
            </label>
            {modoEdicao ? (
              <div className="space-y-2">
                {/* Input oculto */}
                <input
                  ref={fileInputPanoramicaRef}
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      await handleUploadFotoInformacaoMaquina(file, 'panoramica');
                      e.target.value = '';
                    }
                  }}
                  disabled={uploadingPanoramica || !apreciacaoId}
                  className="hidden"
                />
                {/* Área unificada: arraste e solte + clique para selecionar */}
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer text-center flex flex-col items-center justify-center min-h-[100px] ${
                    draggingPanoramica
                      ? 'border-blue-500 bg-blue-50 dark:bg-schmersal-gray-dark'
                      : 'border-gray-300 dark:border-schmersal-gray-dark hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark/50'
                  } ${(uploadingPanoramica || !apreciacaoId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => {
                    if (!uploadingPanoramica && apreciacaoId) {
                      fileInputPanoramicaRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDraggingPanoramica(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nextTarget = e.relatedTarget as Node | null;
                    if (nextTarget && e.currentTarget.contains(nextTarget)) return;
                    setDraggingPanoramica(false);
                  }}
                  onDrop={(e) => handleDropFotoInformacaoMaquina(e, 'panoramica')}
                >
                  <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('apreciacaoEdicao.dadosMaquina.fotos.arrasteSolteOu')} <span className="text-blue-600 dark:text-blue-400 font-medium">{t('apreciacaoEdicao.dadosMaquina.fotos.cliqueSelecionar')}</span>
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">JPG, PNG, HEIC</p>
                </div>
                {uploadingPanoramica && (
                  <p className="text-xs text-blue-500 animate-pulse">{t('apreciacaoEdicao.dadosMaquina.fotos.enviando')}</p>
                )}
                {dados.fotoPanoramicaUrl && (
                  <div className="mt-2">
                    <ImagemComFallback
                      src={dados.fotoPanoramicaUrl}
                      alt={t('apreciacaoEdicao.dadosMaquina.fotos.altPanoramica')}
                      className="max-w-full h-32 object-contain rounded-lg border border-gray-300 dark:border-schmersal-gray-dark"
                    />
                    <button
                      type="button"
                      onClick={() => onChange?.('fotoPanoramicaUrl', '')}
                      className="mt-1 text-xs text-red-500 hover:text-red-700"
                    >
                      {t('comum.acao.remover')}
                    </button>
                  </div>
                )}
                {!apreciacaoId && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('apreciacaoEdicao.dadosMaquina.upload.salvePrimeiro')}
                  </p>
                )}
              </div>
            ) : (
              <div className="min-h-32 flex items-center justify-center">
                {dados.fotoPanoramicaUrl ? (
                  <ImagemComFallback
                    src={dados.fotoPanoramicaUrl}
                    alt={t('apreciacaoEdicao.dadosMaquina.fotos.altPanoramica')}
                    className="max-w-full max-h-32 object-contain rounded-lg border border-gray-300 dark:border-schmersal-gray-dark"
                  />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.arquivo.nenhumEscolhido')}</p>
                )}
              </div>
            )}
          </div>

          {/* Foto de identificação */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.fotos.identificacao')}
            </label>
            {modoEdicao ? (
              <div className="space-y-2">
                {/* Input oculto */}
                <input
                  ref={fileInputIdentificacaoRef}
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      await handleUploadFotoInformacaoMaquina(file, 'identificacao');
                      e.target.value = '';
                    }
                  }}
                  disabled={uploadingIdentificacao || !apreciacaoId}
                  className="hidden"
                />
                {/* Área unificada + botão criar */}
                <div className="flex gap-2">
                  {/* Drag-and-drop + clique */}
                  <div
                    className={`flex-1 border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer text-center flex flex-col items-center justify-center min-h-[100px] ${
                      draggingIdentificacao
                        ? 'border-blue-500 bg-blue-50 dark:bg-schmersal-gray-dark'
                        : 'border-gray-300 dark:border-schmersal-gray-dark hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark/50'
                    } ${(uploadingIdentificacao || !apreciacaoId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => {
                      if (!uploadingIdentificacao && apreciacaoId) {
                        fileInputIdentificacaoRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDraggingIdentificacao(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const nextTarget = e.relatedTarget as Node | null;
                      if (nextTarget && e.currentTarget.contains(nextTarget)) return;
                      setDraggingIdentificacao(false);
                    }}
                    onDrop={(e) => handleDropFotoInformacaoMaquina(e, 'identificacao')}
                  >
                    <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('apreciacaoEdicao.dadosMaquina.fotos.arrasteSolteOu')} <span className="text-blue-600 dark:text-blue-400 font-medium">{t('apreciacaoEdicao.dadosMaquina.fotos.cliqueSelecionar')}</span>
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">JPG, PNG, HEIC</p>
                  </div>
                  {/* Botão criar identificação */}
                  <button
                    type="button"
                    onClick={handleCriarIdentificacao}
                    disabled={gerandoIdentificacao || uploadingIdentificacao || !apreciacaoId}
                    className="flex flex-col items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 dark:border-blue-600
                             rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40
                             transition-colors text-blue-600 dark:text-blue-400
                             disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
                    title={t('apreciacaoEdicao.dadosMaquina.identificacao.tituloBotao')}
                  >
                    <PlusCircle className="h-6 w-6 mb-1" />
                    <span className="text-xs font-medium leading-tight text-center whitespace-pre-line">
                      {gerandoIdentificacao ? t('apreciacaoEdicao.dadosMaquina.identificacao.gerando') : t('apreciacaoEdicao.dadosMaquina.identificacao.criarBotao')}
                    </span>
                  </button>
                </div>

                {/* Card inline para preencher dados da identificação */}
                {mostrarFormIdentificacao && (
                  <div className="border border-blue-300 dark:border-blue-600 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-white">{t('apreciacaoEdicao.dadosMaquina.identificacao.tituloForm')}</p>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('apreciacaoEdicao.dadosMaquina.identificacao.labelDescricao')}
                      </label>
                      <input
                        type="text"
                        value={formIdentificacao.descricao}
                        onChange={(e) => setFormIdentificacao(prev => ({ ...prev, descricao: e.target.value }))}
                        placeholder={t('apreciacaoEdicao.dadosMaquina.identificacao.placeholderDescricao')}
                        className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                                 focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                                 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('apreciacaoEdicao.dadosMaquina.identificacao.labelSismana')}
                      </label>
                      <input
                        type="text"
                        value={formIdentificacao.sismana}
                        onChange={(e) => setFormIdentificacao(prev => ({ ...prev, sismana: e.target.value }))}
                        placeholder={t('apreciacaoEdicao.dadosMaquina.identificacao.placeholderSismana')}
                        className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                                 focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                                 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (!formIdentificacao.descricao.trim() && !formIdentificacao.sismana.trim()) {
                            alert(t('apreciacaoEdicao.dadosMaquina.identificacao.preenchaUmCampo'));
                            return;
                          }
                          gerarFotoIdentificacao(formIdentificacao.descricao.trim(), formIdentificacao.sismana.trim());
                        }}
                        disabled={gerandoIdentificacao}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700
                                 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {gerandoIdentificacao ? t('apreciacaoEdicao.dadosMaquina.identificacao.gerando') : t('apreciacaoEdicao.dadosMaquina.identificacao.gerarImagem')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setMostrarFormIdentificacao(false)}
                        className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-schmersal-gray-darkest
                                 border border-gray-300 dark:border-schmersal-gray-dark rounded-md hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark transition-colors"
                      >
                        {t('comum.acao.cancelar')}
                      </button>
                    </div>
                  </div>
                )}

                {(uploadingIdentificacao || gerandoIdentificacao) && (
                  <p className="text-xs text-blue-500 animate-pulse">
                    {gerandoIdentificacao ? t('apreciacaoEdicao.dadosMaquina.identificacao.gerandoImagem') : t('apreciacaoEdicao.dadosMaquina.fotos.enviando')}
                  </p>
                )}
                {dados.fotoIdentificacaoUrl && (
                  <div className="mt-2">
                    <ImagemComFallback
                      src={dados.fotoIdentificacaoUrl}
                      alt={t('apreciacaoEdicao.dadosMaquina.fotos.altIdentificacao')}
                      className="max-w-full h-32 object-contain rounded-lg border border-gray-300 dark:border-schmersal-gray-dark"
                    />
                    <button
                      type="button"
                      onClick={() => onChange?.('fotoIdentificacaoUrl', '')}
                      className="mt-1 text-xs text-red-500 hover:text-red-700"
                    >
                      {t('comum.acao.remover')}
                    </button>
                  </div>
                )}
                {!apreciacaoId && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('apreciacaoEdicao.dadosMaquina.upload.salvePrimeiro')}
                  </p>
                )}
              </div>
            ) : (
              <div className="min-h-32 flex items-center justify-center">
                {dados.fotoIdentificacaoUrl ? (
                  <ImagemComFallback
                    src={dados.fotoIdentificacaoUrl}
                    alt={t('apreciacaoEdicao.dadosMaquina.fotos.altIdentificacao')}
                    className="max-w-full max-h-32 object-contain rounded-lg border border-gray-300 dark:border-schmersal-gray-dark"
                  />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.arquivo.nenhumEscolhido')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* C.1.1 a C.1.7 - Identificação da Máquina */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.dadosMaquina.c1.titulo')}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.tag')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.identificacaoMaquina || ''}
                  onChange={(e) => onChange?.('identificacaoMaquina', e.target.value)}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.identificacaoMaquina || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.localizacao')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.maquina?.local || ''}
                  onChange={(e) => onChange?.('maquina', { ...dados.maquina, local: e.target.value })}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.local || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.fabricante')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.maquina?.fabricante || ''}
                  onChange={(e) => onChange?.('maquina', { ...dados.maquina, fabricante: e.target.value })}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.fabricante || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.modelo')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.maquina?.modelo || ''}
                  onChange={(e) => onChange?.('maquina', { ...dados.maquina, modelo: e.target.value })}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.modelo || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.serie')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.maquina?.serie || ''}
                  onChange={(e) => onChange?.('maquina', { ...dados.maquina, serie: e.target.value })}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.serie || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.ano')}
              </label>
              {modoEdicao ? (
                <input
                  type="number"
                  value={dados.maquina?.ano || ''}
                  onChange={(e) => onChange?.('maquina', { ...dados.maquina, ano: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.ano || '-'}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c1.descricao')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.maquina?.descricao || ''}
                onChange={(e) => onChange?.('maquina', { ...dados.maquina, descricao: e.target.value })}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.descricao || '-'}</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.tensaoAlimentacao')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.tensaoAcionamento || ''}
                  onChange={(e) => onChange?.('tensaoAcionamento', e.target.value)}
                  placeholder={t('apreciacaoEdicao.dadosMaquina.c1.placeholderTensaoAlimentacao')}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.tensaoAcionamento || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                {t('apreciacaoEdicao.dadosMaquina.c1.tensaoComando')}
              </label>
              {modoEdicao ? (
                <input
                  type="text"
                  value={dados.tensaoComando || ''}
                  onChange={(e) => onChange?.('tensaoComando', e.target.value)}
                  placeholder={t('apreciacaoEdicao.dadosMaquina.c1.placeholderTensaoComando')}
                  className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-white">{dados.tensaoComando || '-'}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* C.2.1 a C.2.5 - Responsável pela máquina */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.dadosMaquina.c2.titulo')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c2.nome')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.operadorEntrevistado || ''}
                onChange={(e) => onChange?.('operadorEntrevistado', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.operadorEntrevistado || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c2.funcao')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.cargoOperador || ''}
                onChange={(e) => onChange?.('cargoOperador', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.cargoOperador || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c2.area')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.area || ''}
                onChange={(e) => onChange?.('maquina', { ...dados.maquina, area: e.target.value })}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.area || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c2.dataEntrevista')}
            </label>
            {modoEdicao ? (
              <input
                type="date"
                value={dados.dataEntrevista ? new Date(dados.dataEntrevista).toISOString().split('T')[0] : ''}
                onChange={(e) => onChange?.('dataEntrevista', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.dataEntrevista ? formatarData(dados.dataEntrevista, { timeZone: 'UTC' }) : '-'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c2.empresa')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.site || ''}
                onChange={(e) => onChange?.('maquina', { ...dados.maquina, site: e.target.value })}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.maquina?.site || '-'}</p>
            )}
          </div>
        </div>
      </div>

      {/* C.3.1 a C.3.4 - Limites da máquina */}
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.dadosMaquina.c3.titulo')}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c3.uso')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesUso || ''}
                onChange={(e) => onChange?.('limitesUso', e.target.value)}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.limitesUso || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c3.espaco')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesEspaco || ''}
                onChange={(e) => onChange?.('limitesEspaco', e.target.value)}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.limitesEspaco || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c3.tempo')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesAdicionais || ''}
                onChange={(e) => onChange?.('limitesAdicionais', e.target.value)}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.limitesAdicionais || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.dadosMaquina.c3.historicoAcidentes')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.historicoAcidentes || ''}
                onChange={(e) => onChange?.('historicoAcidentes', e.target.value)}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.historicoAcidentes || '-'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Lista de verificação administrativa */}
      <ListaVerificacaoComponent
        dados={dados.listaVerificacaoAdministrativa}
        onChange={handleListaVerificacaoChange}
        modoEdicao={modoEdicao}
      />
    </div>
  );
}
